import { CLOUDINARY_CLOUD, CLOUDINARY_PRESET, RODAPE, PDF_CAMPOS } from "../constants";
import { runTransaction, doc } from "firebase/firestore";

// ─── Formatadores ───
export function formatBRL(v) {
  const n = parseFloat(v);
  if (!n) return "";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

export function formatTel(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function telParaWhatsapp(tel) {
  if (!tel) return "";
  const d = tel.replace(/\D/g, "");
  return d.length >= 10 ? `55${d}` : "";
}

// ─── Helpers de imóvel ───
export const isLote = (im) => tipoEhLotePorNome(im?.tipo);

export function comportamentoTipo(nomeTipo, tipos) {
  const t = (tipos || []).find(x => x.nome === nomeTipo);
  if (t?.comportamento) return t.comportamento;
  // Fallback por palavra-chave no nome (pega "Lote Comercial", "Lote em Condomínio", "Terreno Comercial" etc.)
  const n = String(nomeTipo || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (n.includes("lote") || n.includes("terreno") || n.includes("gleba") || n.includes("loteamento") || /\barea\b/.test(n)) return "terreno";
  if (n.includes("casa") || n.includes("apartamento") || n.includes("sobrado") || n.includes("cobertura") || n.includes("kitnet") || n.includes("studio") || n.includes("flat") || n.includes("loft")) return "construcao";
  return "simples";
}
export const ehTerreno = (nomeTipo, tipos) => comportamentoTipo(nomeTipo, tipos) === "terreno";
export const ehConstrucao = (nomeTipo, tipos) => comportamentoTipo(nomeTipo, tipos) === "construcao";
export const isLocacao = (im) => im?.transacao === "Locação";
export const isVenda = (im) => im?.transacao === "Venda" || im?.transacao === "Venda e Locação";

export function statusDoImovel(im) {
  return im?.status || "Disponível";
}

// Visibilidade no site público. O campo "visibilidade" pode ocultar o imóvel
// do site mesmo estando Disponível. "Ocultar do site" e "Ocultar de tudo"
// removem da vitrine pública; os demais valores (ou vazio) mantêm visível.
export function apareceNoSite(im) {
  const v = (im?.visibilidade || "").trim();
  return v !== "Ocultar do site" && v !== "Ocultar de tudo";
}

// Visibilidade nos PORTAIS (feeds Canal Pro / Chaves na Mão / Catálogo Meta).
// Espelha apareceNoSite, mas pro lado dos portais: "Ocultar dos portais" mantém
// o imóvel no site e o tira dos feeds; "Ocultar de tudo" tira de tudo. Os demais
// valores (ou vazio) mantêm o imóvel nos feeds dos portais.
export function apareceNosPortais(im) {
  const v = (im?.visibilidade || "").trim();
  return v !== "Ocultar dos portais" && v !== "Ocultar de tudo";
}

export function totalLocacao(im) {
  return (parseFloat(im?.valorAluguel) || 0) + (parseFloat(im?.valorCondominio) || 0) + (parseFloat(im?.valorIPTU) || 0);
}

// ─── Código do imóvel (baseado no bairro/setor, com numeração) ───
// Regra: o código padrão é o nome do bairro. Se já houver imóvel(is) com código
// daquele bairro, numera os próximos (primeiro sem número, depois " 2", " 3"...).
// Ex: "Rosa dos Ventos", "Rosa dos Ventos 2", "Rosa dos Ventos 3".
// - bairro: nome do bairro do imóvel atual
// - imoveis: lista completa de imóveis (para contar quantos já têm código do bairro)
// - idAtual: id do imóvel sendo editado (ignora ele mesmo na contagem)
export function gerarCodigoImovel(bairro, imoveis, idAtual) {
  const base = (bairro || "").trim();
  if (!base) return "";
  // Regex que casa "Bairro" ou "Bairro N" (com espaço e número no fim)
  const escapar = base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp("^" + escapar + "(?:\\s+(\\d+))?$", "i");
  let maior = 0;          // maior número já usado naquele bairro
  let temBase = false;    // se já existe o código sem número (o "1" implícito)
  for (const im of (imoveis || [])) {
    if (idAtual && im.id === idAtual) continue; // ignora o próprio
    const cod = (im.codigo || "").trim();
    if (!cod) continue;
    const m = cod.match(re);
    if (!m) continue;
    if (m[1]) { const n = parseInt(m[1], 10); if (n > maior) maior = n; }
    else { temBase = true; }
  }
  if (!temBase && maior === 0) return base;        // primeiro do bairro
  const proximo = Math.max(maior, temBase ? 1 : 0) + 1;
  return `${base} ${proximo}`;
}

// Normaliza o nome do bairro para usar como ID do documento de contador.
// (minúsculas, sem acento, espaços colapsados). Ex: "Rosa dos Ventos" -> "rosa dos ventos"
export function chaveBairro(bairro) {
  return (bairro || "").trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

// Reserva (de forma ATÔMICA) o próximo código para um bairro, usando um contador
// persistente no Firestore (coleção "contadores"). O contador só CRESCE — nunca
// reutiliza número, mesmo que imóveis sejam excluídos. Garante unicidade global.
// Primeiro do bairro = só "{Bairro}"; demais = "{Bairro} N".
// - db: instância do Firestore
// - bairro: nome do bairro (mantém capitalização original no código final)
// Retorna o código string (ex: "Rosa dos Ventos" ou "Rosa dos Ventos 4").
export async function reservarCodigoImovel(db, bairro, usados) {
  const base = (bairro || "").trim();
  if (!base) return "";
  const chave = chaveBairro(base);
  const ref = doc(db, "contadores", chave);
  // Reserva o próximo número no contador atômico do bairro.
  let seq = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const atual = snap.exists() ? (snap.data().seq || 0) : 0;
    const novo = atual + 1;
    tx.set(ref, { seq: novo, bairro: base }, { merge: true });
    return novo;
  });
  // Anti-duplicação: se o código já existe (contador dessincronizado), avança até achar um livre.
  const jaUsado = (cod) => usados instanceof Set && usados.has((cod || "").trim().toLowerCase());
  let cod = `${base} ${seq}`;
  let guarda = 0;
  while (jaUsado(cod) && guarda < 1000) {
    seq += 1;
    cod = `${base} ${seq}`;
    guarda += 1;
  }
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const atual = snap.exists() ? (snap.data().seq || 0) : 0;
    if (seq > atual) tx.set(ref, { seq, bairro: base }, { merge: true });
  });
  if (usados instanceof Set) usados.add(cod.trim().toLowerCase());
  // SEMPRE com número (inclusive o primeiro): "Jardins Versalhes 1".
  return cod;
}

// Inicializa o contador de um bairro com um valor mínimo (usado na migração,
// para que os contadores reflitam os códigos já existentes). Só aumenta, nunca diminui.
export async function ajustarContadorMinimo(db, bairro, minimo) {
  const base = (bairro || "").trim();
  if (!base || !minimo) return;
  const ref = doc(db, "contadores", chaveBairro(base));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const atual = snap.exists() ? (snap.data().seq || 0) : 0;
    if (minimo > atual) tx.set(ref, { seq: minimo, bairro: base }, { merge: true });
  });
}

// ─── Geração de descrição automática ───
// Reconhece tipos de lote/terreno pelo NOME — pega "Lote em Condomínio",
// "Lote Comercial", "Área Comercial", "Terreno" etc., não só "Lote"/"Área".
export function tipoEhLotePorNome(tipo) {
  return /lote|terreno|gleba|loteamento|[aá]rea/.test((tipo || "").toLowerCase());
}

export function gerarDescricao(form) {
  const isLoteForm = tipoEhLotePorNome(form.tipo);
  const isRuralForm = /ch[áa]cara|s[íi]tio|fazenda|rancho|haras/i.test(form.tipo || "");
  const linhas = [];
  if (form.titulo) linhas.push(form.titulo);
  linhas.push("");
  if (form.bairro) linhas.push(form.bairro.toUpperCase());
  linhas.push("");
  // Lote/terreno não tem construção: nunca emite "m² de construção" pra esses tipos.
  if (form.metragem && !isLoteForm) linhas.push(`- ${form.metragem} m² de construção`);
  if (form.metragemTotal) linhas.push(`- ${form.metragemTotal} m² de terreno`);
  // medidas/dimensões do lote logo abaixo da metragem
  if (form.retangular && form.frente && form.laterais) linhas.push(`- ${form.frente}x${form.laterais} m`);
  else if (form.medidas) linhas.push(`- ${form.medidas}`);
  const q = parseInt(form.quartos) || 0;
  const s = parseInt(form.suites) || 0;
  const g = parseInt(form.garagens) || 0;
  if (q > 0) linhas.push(`- ${q} quarto${q > 1 ? "s" : ""}${s > 0 ? `, sendo ${s} suíte${s > 1 ? "s" : ""}` : ""}`);
  else if (s > 0) linhas.push(`- ${s} suíte${s > 1 ? "s" : ""}`);
  if (g > 0) linhas.push(`- ${g} ${g > 1 ? "garagens" : "garagem"}`);
  if (isLoteForm || isRuralForm) {
    if (form.asfalto) linhas.push("- Asfalto");
    else linhas.push("- Não tem asfalto");
    if (form.agua) linhas.push("- Água");
    if (form.esgoto) linhas.push("- Esgoto");
    if (form.declive === "Plano") linhas.push("- Plano");
    else if (form.declive) linhas.push(`- Declive: ${form.declive}`);
    if (form.muro) linhas.push("- Murado");
    if (form.esquina) linhas.push("- Esquina");
  }
  if (form.condominio && form.nomeCondominio) linhas.push(`- Condomínio: ${form.nomeCondominio}`);
  if (form.estadoImovel === "Imóvel Novo") linhas.push(`- ${form.estadoImovel}`);
  // Redondezas: o que tem por perto (cada linha vira um tópico).
  if (form.redondezas) {
    linhas.push(...String(form.redondezas).split("\n").map(x => x.trim()).filter(Boolean).map(l => l.startsWith("-") ? l : `- ${l}`));
  }
  if (form.extras) {
    // não repetir o valor de venda: extras às vezes traz "Venda: R$..." ou "R$ ..." (já sai abaixo)
    const ehLinhaPreco = (l) => /^-?\s*(venda|[áa]gio|valor de venda|pre[çc]o)\b/i.test(l) || /^-?\s*r\$\s*\d/i.test(l);
    linhas.push(...form.extras.split("\n").map(x => x.trim()).filter(Boolean).filter(l => !ehLinhaPreco(l)).map(l => l.startsWith("-") ? l : `- ${l}`));
  }
  linhas.push("");
  const loc = form.transacao === "Locação";
  const ven = form.transacao === "Venda" || form.transacao === "Venda e Locação";
  if (ven && parseFloat(form.preco)) {
    // Ágio: troca o rótulo "Venda" por "Ágio" (imóvel financiado/em consórcio).
    const rotuloVenda = form._agio ? "Ágio" : "Venda";
    linhas.push(`${rotuloVenda}: ${formatBRL(form.preco)}`);
    // Dados do ágio: parcela, prazo, saldo devedor e valor total (ágio + saldo).
    if (form._agio) {
      if (parseFloat(form.agioParcela)) linhas.push(`Parcelas de ${formatBRL(form.agioParcela)}`);
      if (parseFloat(form.agioPrazo)) linhas.push(`Faltam ${parseInt(form.agioPrazo)} meses para quitação`);
      if (parseFloat(form.agioSaldoDevedor)) linhas.push(`Saldo devedor: ${formatBRL(form.agioSaldoDevedor)}`);
      const totalAgio = (parseFloat(form.preco) || 0) + (parseFloat(form.agioSaldoDevedor) || 0);
      if (totalAgio) linhas.push(`Valor total: ${formatBRL(totalAgio)}`);
    }
    if (parseFloat(form.valorAvaliacao)) linhas.push(`Avaliado em ${formatBRL(form.valorAvaliacao)}`);
    if (parseFloat(form.valorEntrada)) linhas.push(`Entrada: ${formatBRL(form.valorEntrada)}`);
  }
  if (loc) {
    const a = parseFloat(form.valorAluguel) || 0;
    const c = parseFloat(form.valorCondominio) || 0;
    const ip = parseFloat(form.valorIPTU) || 0;
    if (a) linhas.push(`Aluguel: ${formatBRL(a)}`);
    if (c) linhas.push(`Condomínio: ${formatBRL(c)}/mês`);
    if (ip) linhas.push(`IPTU: ${formatBRL(ip)}/mês`);
    // Total só faz sentido quando há mais de um componente (aluguel + condomínio e/ou IPTU).
    // Se é só o aluguel, repetir o mesmo número como "total" fica redundante.
    const total = a + c + ip;
    if (total && (c || ip)) linhas.push(`Total locação: ${formatBRL(total)}/mês`);
  }
  // Preço sob consulta: sem valor de venda nem de aluguel preenchido.
  const _semVenda = !(ven && parseFloat(form.preco));
  const _semAluguel = !(loc && parseFloat(form.valorAluguel));
  if (_semVenda && _semAluguel) linhas.push("Preço sob consulta");
  if (form.condicoes?.length) {
    // "À vista" é óbvio, não entra. Prefixo "Aceita ..." e em minúsculo.
    const conds = form.condicoes
      .filter(c => c !== "À vista")
      .map(c => c === "Permuta" && String(form.permuta || "").trim() ? `permuta em ${String(form.permuta).trim()}` : String(c).toLowerCase());
    if (conds.length) linhas.push(`Aceita ${conds.join(", ")}`);
  }
  if (form.condominio && parseFloat(form.valorCondominioMensal)) linhas.push(`Condomínio: ${formatBRL(form.valorCondominioMensal)}/mês`);
  linhas.push("");
  linhas.push(RODAPE);
  return linhas.join("\n");
}

export function temRodape(desc) {
  if (!desc) return false;
  return /valores e condi[çc][õo]es/i.test(desc);
}

export function descricaoCompleta(im) {
  const desc = im.descricao || "";
  if (temRodape(desc)) return desc;
  return desc + (desc ? "\n\n" : "") + RODAPE;
}

// Link de localização SEMPRE atualizado: usa a coordenada atual (pino + satélite).
// Só cai no mapsLink salvo se o imóvel não tiver coordenada (imóveis antigos).
export function linkLocalizacao(im) {
  if (im && im.latitude && im.longitude && !isNaN(parseFloat(im.latitude)) && !isNaN(parseFloat(im.longitude))) {
    return `https://www.google.com/maps/place/${im.latitude},${im.longitude}/@${im.latitude},${im.longitude},18z/data=!3m1!1e3`;
  }
  return im && im.mapsLink ? im.mapsLink : null;
}

export function descricaoPronta(im) {
  let txt = descricaoCompleta(im);
  if (im.fotos?.length) {
    const galeria = `https://fotosdoimovel.netlify.app/fotos/${im.id}`;
    txt += `\n\nFotos:\n${galeria}`;
  }

  const loc = linkLocalizacao(im);
  if (loc) txt += `\n\nLocalização:\n${loc}`;
  return txt;
}

// ─── Geração de PDF ───
export function gerarPDF(imoveis, camposSel, titulo = "Lista de Imóveis") {
  const COR_P = "#C0392B";
  const lote = im => tipoEhLotePorNome(im.tipo);
  const isLoc = im => (im.transacao || "").includes("Locação");
  const isVen = im => (im.transacao || "").includes("Venda");

  // Ficha completa para imóvel único; tabela para múltiplos
  const fichaUnica = imoveis.length === 1;

  const fichaHtml = (im) => {
    const fotos = (im.fotos || []).filter(Boolean);
    const fotosHtml = fotos.length ? `
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin:12px 0;page-break-inside:avoid">
        ${fotos.map((f, i) => `<img src="${f}" style="width:100%;height:140px;object-fit:cover;border-radius:6px;${i===0?'grid-column:span 3;height:240px':''}" />`).join("")}
      </div>` : "";

    const row = (l, v) => v ? `<tr><td style="color:#888;font-size:11px;width:140px;padding:4px 8px">${l}</td><td style="font-size:12px;font-weight:600;padding:4px 8px">${v}</td></tr>` : "";
    const total = (parseFloat(im.valorAluguel)||0)+(parseFloat(im.valorCondominio)||0)+(parseFloat(im.valorIPTU)||0);

    return `
      <div style="font-family:Arial,sans-serif;max-width:700px;margin:0 auto;padding:20px">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;border-bottom:2px solid ${COR_P};padding-bottom:12px">
          <div>
            <h1 style="margin:0;font-size:20px;color:${COR_P}">${im.titulo || im.tipo || "Imóvel"}</h1>
            ${im.bairro || im.cidade ? `<p style="margin:4px 0 0;font-size:13px;color:#666">${[im.bairro,im.cidade].filter(Boolean).join(", ")}</p>` : ""}
          </div>
          ${im.codigo ? `<span style="font-size:12px;font-weight:700;color:${COR_P};border:1px solid ${COR_P};padding:4px 10px;border-radius:6px">CÓD: ${String(im.codigo).toUpperCase()}</span>` : ""}
        </div>

        ${fotosHtml}

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:8px">
          <div>
            <p style="margin:0 0 8px;font-size:11px;font-weight:700;color:${COR_P};text-transform:uppercase;letter-spacing:0.5px">Características</p>
            <table style="width:100%;border-collapse:collapse">
              ${row("Tipo", im.tipo)}
              ${row("Transação", im.transacao)}
              ${row("Estado", im.estadoImovel)}
              ${row("Metragem", im.metragem ? im.metragem+" m²" : null)}
              ${row("Terreno", im.metragemTotal ? im.metragemTotal+" m²" : null)}
              ${!lote(im) ? row("Quartos", im.quartos) : ""}
              ${!lote(im) ? row("Suítes", im.suites) : ""}
              ${!lote(im) ? row("Banheiros", im.banheiros) : ""}
              ${row("Garagens", im.garagens)}
              ${lote(im) ? row("Asfalto", im.asfalto ? "Sim" : null) : ""}
              ${lote(im) ? row("Água", im.agua ? "Sim" : null) : ""}
              ${lote(im) ? row("Esgoto", im.esgoto ? "Sim" : null) : ""}
              ${lote(im) ? row("Muro", im.muro ? "Sim" : null) : ""}
              ${lote(im) && im.retangular && im.frente && im.laterais ? row("Medidas", `${im.frente}x${im.laterais}m`) : ""}
            </table>
          </div>
          <div>
            <p style="margin:0 0 8px;font-size:11px;font-weight:700;color:${COR_P};text-transform:uppercase;letter-spacing:0.5px">Valores</p>
            <table style="width:100%;border-collapse:collapse">
              ${isVen(im) ? row("Preço de venda", formatBRL(im.preco)) : ""}
              ${isLoc(im) ? row("Aluguel", formatBRL(im.valorAluguel)) : ""}
              ${row("Condomínio", formatBRL(im.valorCondominio))}
              ${row("IPTU", formatBRL(im.valorIPTU))}
              ${total > 0 && isLoc(im) ? row("Total/mês", formatBRL(total)) : ""}
              ${row("Avaliação", formatBRL(im.valorAvaliacao))}
              ${row("Entrada", formatBRL(im.valorEntrada))}
            </table>
            ${linkLocalizacao(im) ? `<a href="${linkLocalizacao(im)}" style="display:inline-block;margin-top:12px;font-size:12px;color:${COR_P}">📍 Ver no Google Maps</a>` : ""}
          </div>
        </div>

        ${im.descricao ? `
        <div style="margin-top:16px;page-break-inside:avoid">
          <p style="margin:0 0 8px;font-size:11px;font-weight:700;color:${COR_P};text-transform:uppercase;letter-spacing:0.5px">Descrição</p>
          <p style="font-size:12px;color:#444;line-height:1.6;white-space:pre-wrap">${im.descricao}</p>
        </div>` : ""}

        <div style="margin-top:20px;padding-top:12px;border-top:1px solid #eee;font-size:10px;color:#aaa;text-align:center">
          Inerente Gestão Imobiliária — gerado em ${new Date().toLocaleDateString("pt-BR")}
        </div>
      </div>`;
  };

  // Para múltiplos imóveis, mantém tabela compacta
  const tabelaHtml = () => {
    const has = k => camposSel.includes(k);
    const rows = imoveis.map(im => {
      const total = (parseFloat(im.valorAluguel)||0)+(parseFloat(im.valorCondominio)||0)+(parseFloat(im.valorIPTU)||0);
      return `<tr>
        ${has("tipo") ? `<td>${im.tipo||""} / ${im.transacao||""}</td>` : ""}
        ${has("status") ? `<td>${im.status||"Disponível"}</td>` : ""}
        ${has("cidade") ? `<td>${im.cidade||""}</td>` : ""}
        ${has("bairro") ? `<td>${im.bairro||""}</td>` : ""}
        ${has("metragem") ? `<td>${im.metragem ? im.metragem+" m²" : ""}</td>` : ""}
        ${has("quartos") ? `<td>${im.quartos||""}</td>` : ""}
        ${has("preco") ? `<td>${isLoc(im) ? (formatBRL(im.valorAluguel)||"") : (formatBRL(im.preco)||"")}</td>` : ""}
        ${has("total") ? `<td>${total > 0 ? formatBRL(total) : ""}</td>` : ""}
      </tr>`;
    }).join("");
    const headers = PDF_CAMPOS.filter(c => has(c.key)).map(c => `<th>${c.label}</th>`).join("");
    return `<h2 style="color:${COR_P}">${titulo}</h2>
      <p style="color:#666;font-size:11px">${imoveis.length} imóvel(is) — ${new Date().toLocaleDateString("pt-BR")}</p>
      <table style="width:100%;border-collapse:collapse;font-size:10px">
        <thead><tr style="background:${COR_P};color:#fff">${headers}</tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
  };

  const bodyHtml = fichaUnica ? fichaHtml(imoveis[0]) : tabelaHtml();
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${titulo}</title>
  <style>
    body{margin:0;padding:0;font-family:Arial,sans-serif}
    table td,table th{border:1px solid #eee;vertical-align:top}
    tr:nth-child(even) td{background:#fdf5f5}
    @media print{body{padding:0}@page{size:A4;margin:10mm}}
  </style>
  </head><body>${bodyHtml}</body></html>`;

  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 800);
}

// ─── Upload Cloudinary ───
export async function uploadToCloudinary(file) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("upload_preset", CLOUDINARY_PRESET);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`, { method: "POST", body: fd });
  const data = await res.json();
  if (!data.secure_url) throw new Error("Falha no upload");
  return data.secure_url;
}

// ─── ViaCEP ───
// Retorna: logradouro, complemento, bairro, localidade, uf
export function buscarCEP(raw, callback) {
  const c = raw.replace(/\D/g, "");
  if (c.length !== 8) return;
  const cbName = `cep_cb_${Date.now()}`;
  window[cbName] = (data) => {
    delete window[cbName];
    document.getElementById(cbName)?.remove();
    if (data && !data.erro) callback(data);
  };
  const s = document.createElement("script");
  s.id = cbName;
  s.src = `https://viacep.com.br/ws/${c}/json/?callback=${cbName}`;
  s.onerror = () => { delete window[cbName]; s.remove(); };
  document.head.appendChild(s);
}

// ─── Geocoding via OpenStreetMap Nominatim ───
// Busca latitude/longitude a partir do endereço do imóvel.
// Gratuito, sem API key. Suficiente para uso manual (1 req por save).
// Retorna { latitude, longitude } como strings ou null se não encontrar.
export async function geocodificarEndereco({ endereco, bairro, cidade, estado, cep }) {
  try {
    const partes = [endereco, bairro, cidade, estado, "Brasil"].filter(Boolean);
    const q = encodeURIComponent(partes.join(", "));
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=br`,
      { headers: { "Accept-Language": "pt-BR" } }
    );
    const data = await res.json();
    if (data && data.length > 0) {
      return {
        latitude: parseFloat(data[0].lat).toFixed(7),
        longitude: parseFloat(data[0].lon).toFixed(7),
      };
    }
    // Fallback: tenta só cidade + estado
    if (cidade) {
      const q2 = encodeURIComponent(`${cidade}, ${estado || ""}, Brasil`);
      const res2 = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${q2}&format=json&limit=1&countrycodes=br`,
        { headers: { "Accept-Language": "pt-BR" } }
      );
      const data2 = await res2.json();
      if (data2 && data2.length > 0) {
        return {
          latitude: parseFloat(data2[0].lat).toFixed(7),
          longitude: parseFloat(data2[0].lon).toFixed(7),
        };
      }
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Match de transação ───
export function matchTransacao(im, filtro) {
  if (filtro === "Todos") return true;
  if (im.transacao === "Venda e Locação") return filtro === "Venda";
  return im.transacao === filtro;
}

// ─── Ordenação ───
export function ordenarImoveis(imoveis, ordem) {
  const arr = [...imoveis];
  switch (ordem) {
    case "antigo": return arr.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    case "preco_menor": return arr.sort((a, b) => precoBase(a) - precoBase(b));
    case "preco_maior": return arr.sort((a, b) => precoBase(b) - precoBase(a));
    case "metragem_menor": return arr.sort((a, b) => metragemBase(a) - metragemBase(b));
    case "metragem_maior": return arr.sort((a, b) => metragemBase(b) - metragemBase(a));
    case "bairro_az": return arr.sort((a, b) => bairroBase(a).localeCompare(bairroBase(b), "pt-BR"));
    case "bairro_za": return arr.sort((a, b) => bairroBase(b).localeCompare(bairroBase(a), "pt-BR"));
    case "recente":
    default: return arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }
}

function precoBase(im) {
  if (im.transacao === "Locação") return parseFloat(im.valorAluguel) || 0;
  return parseFloat(im.preco) || 0;
}

function metragemBase(im) {
  return parseFloat(im.metragem) || parseFloat(im.metragemTotal) || 0;
}

// Bairro normalizado para ordenação. Vazios vão pro fim (caractere alto)
// para não aparecerem antes dos preenchidos na ordem A-Z.
function bairroBase(im) {
  const b = (im.bairro || "").trim();
  return b ? b.toLocaleLowerCase("pt-BR") : "\uffff";
}

// ─── Download de fotos ───
// Celular: abre a folha de compartilhamento (salvar na galeria / mandar no zap).
// PC: um ZIP unico, porque o Chrome bloqueia varios downloads em sequencia.
function slugFoto(txt) {
  return String(txt || "imovel")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "imovel";
}

function extFoto(tipo) {
  if (/png/i.test(tipo || "")) return "png";
  if (/webp/i.test(tipo || "")) return "webp";
  return "jpg";
}

function salvarArquivo(blob, nome) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 5000);
}

function carregarJSZip() {
  if (window.JSZip) return Promise.resolve();
  return new Promise((concluir, falhar) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
    s.onload = () => (window.JSZip ? concluir() : falhar(new Error("jszip")));
    s.onerror = () => falhar(new Error("jszip"));
    document.head.appendChild(s);
  });
}

// E celular/tablet de verdade? (o Windows tambem "compartilha", mas la o certo e ZIP)
function ehCelular() {
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  return (navigator.maxTouchPoints || 0) > 1 && /Mac/i.test(navigator.platform || "");
}

// O aparelho sabe compartilhar arquivos?
function podeCompartilharFotos() {
  try {
    if (!ehCelular()) return false;
    if (!navigator.share || !navigator.canShare || typeof File !== "function") return false;
    const teste = new File([new Blob(["x"], { type: "image/jpeg" })], "t.jpg", { type: "image/jpeg" });
    return navigator.canShare({ files: [teste] });
  } catch { return false; }
}

export async function downloadFotos(im) {
  if (!im.fotos?.length) return alert("Sem fotos.");
  const base = slugFoto(im.titulo);
  const nomeDe = (i, tipo) => base + "-" + String(i + 1).padStart(2, "0") + "." + extFoto(tipo);

  let blobs;
  try {
    blobs = [];
    for (let i = 0; i < im.fotos.length; i++) {
      const res = await fetch(im.fotos[i], { mode: "cors" });
      if (!res.ok) throw new Error("http " + res.status);
      blobs.push(await res.blob());
    }
  } catch (e) {
    console.warn("nao consegui baixar as fotos:", e);
    return alert("Nao consegui baixar as fotos. Tente de novo.");
  }

  // CELULAR: abre a folha do aparelho (Salvar em Fotos / WhatsApp)
  if (podeCompartilharFotos()) {
    try {
      const arquivos = blobs.map((b, i) => new File([b], nomeDe(i, b.type), { type: b.type || "image/jpeg" }));
      if (navigator.canShare({ files: arquivos })) {
        await navigator.share({ files: arquivos, title: im.titulo || "Fotos do imovel" });
      } else {
        for (let i = 0; i < arquivos.length; i += 10) {
          await navigator.share({ files: arquivos.slice(i, i + 10), title: im.titulo || "Fotos do imovel" });
        }
      }
      return;
    } catch (e) {
      if (e && e.name === "AbortError") return;
      console.warn("compartilhar falhou, caindo pro ZIP:", e);
    }
  }

  // PC: um ZIP unico (o Chrome bloqueia varios downloads em sequencia)
  try {
    await carregarJSZip();
    const zip = new window.JSZip();
    blobs.forEach((b, i) => zip.file(nomeDe(i, b.type), b));
    const zipBlob = await zip.generateAsync({ type: "blob", compression: "STORE" });
    salvarArquivo(zipBlob, base + "-fotos.zip");
    return;
  } catch (e) {
    console.warn("ZIP indisponivel, baixando uma a uma:", e);
  }

  // Ultimo recurso: uma a uma
  for (let i = 0; i < blobs.length; i++) {
    salvarArquivo(blobs[i], nomeDe(i, blobs[i].type));
    await new Promise(seguir => setTimeout(seguir, 700));
  }
}

// ─── WhatsApp ───
export function whatsappTudo(im) {
  const galeriaLink = `https://fotosdoimovel.netlify.app/fotos/${im.id}`;
  const loc = linkLocalizacao(im);
  const txt = descricaoCompleta(im) +
    (im.fotos?.length ? `\n\nFotos:\n${galeriaLink}` : "") +
    (loc ? `\n\nLocalização:\n${loc}` : "");
  window.open("https://wa.me/?text=" + encodeURIComponent(txt), "_blank");
}

export function whatsappDescricao(im) {
  window.open("https://wa.me/?text=" + encodeURIComponent(descricaoCompleta(im)), "_blank");
}

export function whatsappMaps(im) {
  const loc = linkLocalizacao(im);
  if (!loc) return alert("Sem localização cadastrada.");
  window.open("https://wa.me/?text=" + encodeURIComponent(`Localização do imóvel:\n${loc}`), "_blank");
}

export function whatsappFotos(im) {
  if (!im.fotos?.length) return alert("Sem fotos.");
  const link = `${window.location.origin}${window.location.pathname}#galeria-${im.id}`;
  window.open("https://wa.me/?text=" + encodeURIComponent(`Fotos do imóvel:\n${link}`), "_blank");
}

export function waContatoImovel(im, empresaWhatsapp) {
  const titulo = im.titulo || "imóvel";
  const link = `${window.location.origin}/imovel/${im.id}`;
  const msg = `Olá! Tenho interesse no imóvel: ${titulo}\n${link}`;
  return `https://wa.me/${empresaWhatsapp}?text=${encodeURIComponent(msg)}`;
}

// ─── Validação para feeds automáticos ───
// Os feeds mapeiam QUALQUER tipo (via comportamento do cadastro central), então
// não existe mais "tipo não suportado": o que importa é o tipo estar preenchido.
const TIPOS_TERRENO_VALIDACAO = ["Lote","Terreno","Área","Sítio","Chácara","Fazenda","Galpão","Depósito"];

// Canais com integração automática via feed XML
export const CANAIS_AUTO = ["Canal Pro", "Chaves na Mão", "Catálogo Meta"];

// Valida se um imóvel atende aos requisitos do canal automático.
// Retorna array de strings com os problemas (vazio = OK).
// Para canais manuais, sempre retorna [].
export function validarParaCanal(im, canal) {
  if (!CANAIS_AUTO.includes(canal)) return [];

  const problemas = [];
  const status = (im.status || "").toLowerCase();
  if (status && status !== "disponível" && status !== "disponivel") {
    problemas.push("Status não está como Disponível");
  }

  // Visibilidade: "Ocultar dos portais" e "Ocultar de tudo" tiram o imóvel dos feeds.
  if (!apareceNosPortais(im)) {
    problemas.push("Visibilidade está ocultando dos portais");
  }

  // Canal desligado de propósito (opt-out explícito ativo:false).
  if (im.anuncios && im.anuncios[canal] && im.anuncios[canal].ativo === false) {
    problemas.push('Canal desligado à mão em "Onde foi anunciado" (remarque para voltar ao feed)');
  }

  const fotos = (im.fotos || []).filter(Boolean);
  const desc = (im.descricao || "").trim();
  const cidade = (im.cidade || "").trim();
  const bairro = (im.bairro || "").trim();
  const estado = (im.estado || "").trim();
  const trans = im.transacao || "";
  const isLocacao = trans === "Locação";
  const isVenda = trans === "Venda" || trans === "Venda e Locação";
  const isLote = TIPOS_TERRENO_VALIDACAO.includes(im.tipo);
  const metragem = parseFloat(im.metragem) || 0;
  const metragemTotal = parseFloat(im.metragemTotal) || 0;
  const area = isLote ? (metragemTotal || metragem) : (metragem || metragemTotal);

  if (!im.tipo) problemas.push("Defina o tipo do imóvel");

  // BLOQUEIOS REAIS: o que de fato IMPEDE o anúncio de subir no portal.
  // (CEP e quartos/banheiros NÃO entram aqui — o feed XML os preenche
  //  automaticamente, então não impedem a publicação. Viram aviso de qualidade
  //  em avisosDoCanal().)
  if (canal === "Canal Pro") {
    if (fotos.length === 0) problemas.push("Adicione pelo menos 1 foto");
    if (desc.length < 50) problemas.push("Descrição precisa ter no mínimo 50 caracteres");
    if (area === 0) problemas.push("Preencha a metragem");
    if (!cidade) problemas.push("Preencha a cidade");
    if (!bairro) problemas.push("Preencha o bairro");
    if (isVenda && !parseFloat(im.preco) && !parseFloat(im.valorFinal)) problemas.push("Preencha o preço de venda");
    if (isLocacao && !parseFloat(im.valorAluguel)) problemas.push("Preencha o valor do aluguel");
    if (!isVenda && !isLocacao) problemas.push("Defina o tipo de transação");
  }

  if (canal === "Chaves na Mão") {
    if (!cidade) problemas.push("Preencha a cidade (o Chaves recusa sem cidade)");
    if (!bairro) problemas.push("Preencha o bairro (o Chaves recusa sem bairro)");
    if (!estado) problemas.push("Preencha o estado (UF)");
    if (!desc) problemas.push("Preencha a descrição");
    if (isVenda && !parseFloat(im.preco) && !parseFloat(im.valorFinal)) problemas.push("Preencha o preço de venda");
    if (isLocacao && !parseFloat(im.valorAluguel)) problemas.push("Preencha o valor do aluguel");
    if (!isVenda && !isLocacao) problemas.push("Defina o tipo de transação");
  }

  if (canal === "Catálogo Meta") {
    if (fotos.length === 0) problemas.push("Adicione pelo menos 1 foto");
    if (!cidade) problemas.push("Preencha a cidade");
    if (!estado) problemas.push("Preencha o estado (UF)");
    if (!parseFloat(im.latitude) || !parseFloat(im.longitude)) problemas.push("Coordenadas não foram encontradas — verifique cidade/bairro");
    if (isVenda && !parseFloat(im.preco) && !parseFloat(im.valorFinal)) problemas.push("Preencha o preço de venda");
    if (isLocacao && !parseFloat(im.valorAluguel)) problemas.push("Preencha o valor do aluguel");
    if (!isVenda && !isLocacao) problemas.push("Defina o tipo de transação");
  }

  return problemas;
}

// Avisos de QUALIDADE — não impedem o anúncio de subir (o feed conserta sozinho),
// mas o portal reclama e baixa a nota. Aparecem na fila como campos pra preencher.
export function avisosDoCanal(im, canal) {
  if (!CANAIS_AUTO.includes(canal)) return [];
  const avisos = [];
  const isLote = TIPOS_TERRENO_VALIDACAO.includes(im.tipo);

  // CEP: o feed preenche com o CEP da cidade quando vazio/inválido, mas o ZAP+
  // dá nota menor. Avisar para melhorar (não bloqueia).
  if (canal === "Canal Pro" || canal === "Chaves na Mão") {
    const cepLimpo = String(im.cep || "").replace(/\D/g, "");
    if (!cepLimpo) avisos.push("CEP vazio — o feed usa o CEP da cidade, mas o ideal é preencher o exato");
    else if (cepLimpo.length !== 8) avisos.push("CEP inválido — precisa ter 8 dígitos");
    // Quartos e banheiros para residencial — o feed força o mínimo 1, mas é melhor informar
    if (!isLote) {
      if (!parseInt(im.quartos)) avisos.push("Informe a quantidade de quartos");
      if (!parseInt(im.banheiros)) avisos.push("Informe a quantidade de banheiros");
    }
  }
  return avisos;
}


// Miniatura do Cloudinary no tamanho pedido (usado na lista do admin e na home publica).
export function fotoThumbUrl(foto, w, h) {
  return (foto && foto.includes("res.cloudinary.com") && foto.includes("/upload/"))
    ? foto.replace("/upload/", `/upload/w_${w},h_${h},c_fill,f_auto,q_auto/`)
    : foto;
}

// Copia texto pro clipboard, com fallback pra navegador antigo. Retorna true se copiou.
export async function copiarTexto(txt) {
  try {
    await navigator.clipboard.writeText(txt);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = txt;
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch {}
    document.body.removeChild(ta);
    return ok;
  }
}
